import React, { Suspense, useEffect } from 'react'
import { Navigate, Outlet, useNavigate } from 'react-router-dom'

import { AccountSettings } from 'Features/accountSettings/routes'
import { UserProfile } from 'Features/userProfile/routes'
import { MainWrapper } from 'Features/MainWrapper/MainWrapper'
import { DomainManagement } from 'Features/domainsManagement/routes'
import { useLogout } from 'Utils/logout'
import { PARAMS_COOKIE_KEYS } from 'Consts/cookies.enum'
import { CookieHelper } from 'Utils/cookieHelper'
import { CheqLoader } from '@clickcease/cc-client-components-lib'
import { PaidMarketingRoutes } from './PaidMarketingRoutes'
import { BotzappingRoutes } from './BotazappingRoutes'
import { Pricing } from 'Features/pricing/routes'
import { GoogleAnalyticsConnector } from 'Features/GoogleAnalyticsConnector/GoogleAnalyticsConnector'
import { Checkout } from 'Features/checkout/views/Checkout/Checkout'
import { OnboardingWizard } from 'Features/OnboardingWizard/OnboardingWizard'
import { useAppStore } from 'Stores/appStoreModuleFed'
import { useAccountStatus } from 'Features/MainWrapper/api/getAccountStatus'
import { BotMitigationCrossSell } from 'Features/CrossSell/BotMitigationCrossSell'
import { PaidMarketingCrossSell } from 'Features/CrossSell/PaidMarketingCrossSell'
import { CrossSellPricing } from 'Features/CrossSell/CrosSellPricing/CrossSellPricing'
import { ModifyPlan } from 'Features/CrossSell/ModifyPlan/ModifyPlan'
import { useLoginStatus } from 'src/hooks/useLoginStatus'
import { useSetLanguage } from 'Hooks/useSetLanguage'
import { lazyImport } from 'Utils/lazyImport'

import { NewHome } from 'Features/home/routes/Home/NewHome'
import { ReactivationPage } from 'Features/ReactivationPage/ReactivationPage'

const { SelectAccount } = lazyImport(
  () => import('Features/AccountSelector/routes/SelectAccount'),
  'SelectAccount'
)

const Wrapper = () => {
  const { logOutUser } = useLogout()
  const { getLogicCookieData } = CookieHelper()
  const navigate = useNavigate()
  const { isLoggedIn } = useLoginStatus()
  const { setLanguage } = useSetLanguage()
  useAccountStatus()
  const { isOnboardingModalOpen, redirectRoute, setRedirectRoute, setOnboardingModal } =
    useAppStore()

  useEffect(() => {
    if (redirectRoute) {
      navigate(`/${redirectRoute}`)
      setTimeout(() => setRedirectRoute(false), 1000)
    } else if (isOnboardingModalOpen) {
      navigate('/onboarding')
      setOnboardingModal(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const subId = getLogicCookieData(PARAMS_COOKIE_KEYS.SUBSCRIPTION_ID)
  const isReactivationValue = getLogicCookieData(PARAMS_COOKIE_KEYS.IS_REACTIVATION)
  const isReactivation = isReactivationValue === 'true'

  useEffect(() => {
    void setLanguage(undefined)
    if (!isLoggedIn) {
      logOutUser()
    } else if (subId === '' && !isReactivation) {
      navigate('/pricing')
    } else if (subId === '' && isReactivation) {
      navigate('/reactivation')
    }
    // eslint-disable-next-line
  }, [])

  return (
    <>
      <MainWrapper>
        <Suspense fallback={<CheqLoader />}>
          <Outlet />
        </Suspense>
      </MainWrapper>
    </>
  )
}

export const protectedRoutes = [
  {
    path: '/',
    element: <Wrapper />,
    children: [{ path: '/', element: <NewHome /> }],
  },
  {
    path: 'onboarding',
    element: <Wrapper />,
    children: [{ path: '', element: <OnboardingWizard /> }],
  },
  {
    path: 'account-settings',
    element: <Wrapper />,
    children: [
      { path: '', element: <AccountSettings /> },
      { path: '*', element: <Navigate to="" /> },
    ],
  },
  {
    path: 'user-profile/*',
    element: <Wrapper />,
    children: [{ path: '*', element: <UserProfile /> }],
  },
  {
    path: 'google-analytics-connector/*',
    element: <Wrapper />,
    children: [{ path: '*', element: <GoogleAnalyticsConnector /> }],
  },
  {
    path: 'domain-management/*',
    element: <Wrapper />,
    children: [{ path: '*', element: <DomainManagement /> }],
  },
  {
    path: 'pricing/*',
    element: <Pricing />,
  },
  {
    path: 'account-selector/*',
    element: <SelectAccount />,
  },
  {
    path: 'paid-marketing/*',
    element: <Wrapper />,
    children: [{ path: '*', fallback: <div />, element: <PaidMarketingRoutes /> }],
  },
  {
    path: 'checkout/*',
    element: <Checkout />,
  },
  {
    path: 'paid-marketing-cross-sell/*',
    element: <Wrapper />,
    children: [{ path: '*', element: <PaidMarketingCrossSell /> }],
  },
  {
    path: 'bot-mitigation-cross-sell/*',
    element: <Wrapper />,
    children: [{ path: '*', element: <BotMitigationCrossSell /> }],
  },
  {
    path: 'cross-sell-pricing/*',
    element: <Wrapper />,
    children: [{ path: '*', element: <CrossSellPricing /> }],
  },
  {
    path: 'modify-plan/*',
    element: <Wrapper />,
    children: [{ path: '*', element: <ModifyPlan /> }],
  },
  {
    path: 'reactivation/*',
    element: <ReactivationPage />,
  },
  {
    path: 'botzapping/*',
    element: <Wrapper />,
    children: [
      {
        path: '*',
        element: <BotzappingRoutes />,
      },
    ],
  },
]
